import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Typography,
    Paper,
    Chip,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Alert,
} from '@mui/material';
import { CiCirclePlus, CiCircleRemove } from "react-icons/ci";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useProductCalculation } from './Hooks/useProductCalculation';
import { validateInputs } from '../../../utils/validation';
import FileUploaderMultiMedia, { DEFAULT_MAX_FILE_SIZE, VIDEO_MAX_FILE_SIZE } from '../../FileUpload/FileUploaderMultiMedia';
import { isVideoUrl } from './ImagePreviewModal';
import { toast } from 'react-toastify';

const IMAGE_ACCEPT = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
};

const VIDEO_ACCEPT = {
    'video/mp4': ['.mp4'],
};

const isValidImageFile = (file) => {
    if (!(file instanceof File)) return true;
    const isAllowedType = ['image/jpeg', 'image/png'].includes(file.type)
        || /\.(jpe?g|png)$/i.test(file.name);
    const isAllowedSize = file.size <= DEFAULT_MAX_FILE_SIZE;
    return isAllowedType && isAllowedSize;
};

const isValidVideoFile = (file) => {
    if (!(file instanceof File)) return true;
    const isAllowedType = file.type === 'video/mp4' || /\.mp4$/i.test(file.name);
    const isAllowedSize = file.size <= VIDEO_MAX_FILE_SIZE;
    return isAllowedType && isAllowedSize;
};

const validateProductMedia = (images = [], videos = []) => {
    for (const file of images) {
        if (file instanceof File) {
            if (!['image/jpeg', 'image/png'].includes(file.type) && !/\.(jpe?g|png)$/i.test(file.name)) {
                return 'Images must be JPG or PNG only';
            }
            if (file.size > DEFAULT_MAX_FILE_SIZE) {
                return `Image "${file.name}" exceeds 500 KB size limit`;
            }
        }
    }

    for (const file of videos) {
        if (file instanceof File) {
            if (file.type !== 'video/mp4' && !/\.mp4$/i.test(file.name)) {
                return 'Videos must be MP4 only';
            }
            if (file.size > VIDEO_MAX_FILE_SIZE) {
                return `Video "${file.name}" exceeds 5 MB size limit`;
            }
        }
    }

    return null;
};

const initialForm = {
    name: '',
    metalName: 'Gold',
    karat: '',
    ratePerGms: 0,
    purity: '',
    metal_weight: '',
    metalCost: '',
    percentageCharges: false,
    makingChargesPercent: 0,
    makingCharges: 0,
    discountMakingPercent: 0,
    gstMaking: 0,
    finalMakingCharges: 0,
    deliveryCharges: 0,
    discountonDelivery: 0,
    gstonDelivery: 0,
    finalCeliveryCharges: 0,
    packingCharges: 0,
    discountonPacking: 0,
    gstonPacking: 0,
    finalPackingCharges: 0,
    hallmarkCharges: 0,
    otherCharges: 0,
    discountProduct: 0,
    tax: 0,
    desc: '',
    finalProductCost: 0,
    tags: [],
    productImages: [],
    productVideos: [],
    id: 'null',
    termandconditions: 'undefined',
    bullion: '',
    bullionOptionKey: '',
    makingchargesType: 'lumson',
};

const getNumeric = (val) => parseFloat(val) || 0;
const getOptionalChargeError = (label, value) => {
    if (value === '' || value === null || value === undefined) return '';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return `${label} must be a valid number`;
    if (parsed < 0) return `${label} cannot be negative`;
    return '';
};

const normalizeRateType = (value) => (value || '').toString().trim().toLowerCase();

const getRatePerGram = (item) => {
    if (!item) return 0;

    const candidates = [
        item?.ratePerGram,
        item?.buyRate?.ratePerGram,
        item?.buyRatePerGram,
        item?.bullion?.buyRate?.ratePerGram,
        item?.sellRate?.ratePerGram,
        item?.bullion?.bullionRate?.ratePerGram,
    ];

    for (const rate of candidates) {
        const numeric = Number(rate);
        if (!Number.isNaN(numeric) && numeric > 0) return numeric;
    }

    return 0;
};

const buildBuyMetalOptions = (metalsenabled = []) => {
    if (!Array.isArray(metalsenabled)) return [];

    return metalsenabled
        .filter((item) => item?.bullion?._id)
        .map((item, index) => ({
            ...item,
            optionKey: item?._id || `${item.bullion._id}-${index}`,
            buyRatePerGram: getRatePerGram(item),
        }));
};

const getBullionId = (bullion) => {
    if (!bullion) return '';
    if (typeof bullion === 'string') return bullion;
    return bullion._id || '';
};

const getStoredProductRate = (editData) => {
    const bullion = editData?.bullion;
    const candidates = [
        typeof bullion === 'object' ? bullion?.bullionRate?.ratePerGram : null,
        editData?.ratePerGrm,
        editData?.ratePerGms,
    ];
    for (const rate of candidates) {
        const numeric = Number(rate);
        if (!Number.isNaN(numeric) && numeric > 0) return numeric;
    }
    return null;
};

const findMetalOptionForEdit = (options, editData) => {
    const bullionId = getBullionId(editData?.bullion);
    if (!bullionId || !options?.length) return null;

    const storedRate = getStoredProductRate(editData);
    const matches = options.filter((item) => item.bullion._id === bullionId);
    if (!matches.length) return null;

    if (storedRate != null) {
        const byRate = matches.find(
            (item) => Math.abs(getRatePerGram(item) - storedRate) < 0.01
        );
        if (byRate) return byRate;
    }

    return matches[0];
};

const formatBuyMetalLabel = (item) => {
    const b = item.bullion;
    const rateType = normalizeRateType(item?.rateType || item?.buyRate?.rateType || item?.sellRate?.rateType) || 'rate';
    return `${b.metalName} | ${b.purity} purity | ${b.karat}K | ${rateType.toUpperCase()} ₹${item.buyRatePerGram}/gm`;
};

const formatAmount = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const buildMetalCostPreview = (formData) => {
    const baseRate = getNumeric(formData.ratePerGms);
    const basePurity = getNumeric(formData.purity);
    const selectedPurity = getNumeric(formData.inputPurity);
    const weight = getNumeric(formData.metal_weight);

    if (!baseRate || !basePurity || !selectedPurity) return null;

    const purityRate = (baseRate * selectedPurity) / basePurity;
    const metalCost = weight > 0 ? weight * purityRate : 0;

    return {
        baseRate,
        basePurity,
        selectedPurity,
        weight,
        purityRate,
        metalCost,
    };
};

const chargeSections = [
    {
        key: 'making',
        title: 'Making Charges',
        subtitle: 'Crafting cost — enter amount, discount, and GST to get final making charges',
        accent: '#752ADA',
        fields: [
            { label: 'Making Charges (₹)', name: 'makingCharges', helperText: 'Base making charge amount' },
            { label: 'Discount (%)', name: 'discountMakingPercent', helperText: 'Discount applied on making' },
            { label: 'GST / Tax (%)', name: 'gstMaking', helperText: 'Tax percentage on making' },
            { label: 'Final Making (₹)', name: 'finalMakingCharges', helperText: 'Auto-calculated final amount', readOnly: true },
        ],
        percentDisabledFields: ['makingCharges', 'discountMakingPercent', 'gstMaking'],
    },
    {
        key: 'delivery',
        title: 'Delivery Charges',
        subtitle: 'Shipping cost — discount and GST are applied to reach final delivery charges',
        accent: '#0284c7',
        fields: [
            { label: 'Delivery Charges (₹)', name: 'deliveryCharges', helperText: 'Base delivery charge' },
            { label: 'Discount (%)', name: 'discountonDelivery', helperText: 'Discount on delivery' },
            { label: 'GST / Tax (%)', name: 'gstonDelivery', helperText: 'Tax on delivery charges' },
            { label: 'Final Delivery (₹)', name: 'finalCeliveryCharges', helperText: 'Auto-calculated final amount', readOnly: true },
        ],
    },
    {
        key: 'packing',
        title: 'Packing Charges',
        subtitle: 'Packaging cost — discount and GST are applied to reach final packing charges',
        accent: '#059669',
        fields: [
            { label: 'Packing Charges (₹)', name: 'packingCharges', helperText: 'Base packing charge' },
            { label: 'Discount (%)', name: 'discountonPacking', helperText: 'Discount on packing' },
            { label: 'GST / Tax (%)', name: 'gstonPacking', helperText: 'Tax on packing charges' },
            { label: 'Final Packing (₹)', name: 'finalPackingCharges', helperText: 'Auto-calculated final amount', readOnly: true },
        ],
    },
];

const ChargeField = ({
    label,
    name,
    value,
    onChange,
    helperText,
    error = false,
    disabled = false,
    readOnly = false,
    accent,
    step = 'any',
}) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography
            variant="caption"
            fontWeight={600}
            sx={{ mb: 0.75, color: '#475569', lineHeight: 1.35, minHeight: 34, display: 'block' }}
        >
            {label}
        </Typography>
        <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0, step }}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            error={error}
            helperText={helperText}
            sx={{
                '& .MuiFormHelperText-root': {
                    minHeight: 32,
                    m: 0,
                    mt: 0.75,
                    lineHeight: 1.35,
                },
                ...(readOnly ? {
                    '& .MuiOutlinedInput-root': {
                        bgcolor: `${accent}10`,
                        fontWeight: 600,
                    },
                } : {}),
            }}
        />
    </Box>
);

const ChargeRow = ({ title, subtitle, accent, topContent, children, columns = 4 }) => (
    <Box
        sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid #e8eaf0',
            bgcolor: '#fafbfc',
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                mb: topContent || children ? 2 : 0,
                pb: 1.25,
                borderBottom: `2px solid ${accent}22`,
            }}
        >
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    mt: 0.6,
                    borderRadius: '50%',
                    bgcolor: accent,
                    flexShrink: 0,
                }}
            />
            <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#1f2937">
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            </Box>
        </Box>
        {topContent}
        {children && (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
                        md: `repeat(${columns}, minmax(0, 1fr))`,
                    },
                    gap: 2,
                    alignItems: 'start',
                    mt: topContent ? 2 : 0,
                }}
            >
                {children}
            </Box>
        )}
    </Box>
);

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            border: '1px solid #e8eaf0',
            bgcolor: '#fff',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(117,42,218,0.1)',
                    color: '#752ADA',
                    flexShrink: 0,
                }}
            >
                <Icon fontSize="small" />
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight={700} color="#1f2937">
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
        {children}
    </Paper>
);

const ProductDialog = ({ open, onClose, onSubmit, editData = null, metalsenabled = [] }) => {
    const [formData, setFormData] = useState(initialForm);
    const [newTag, setNewTag] = useState('');
    const { calculationResult, updateCalculation } = useProductCalculation(formData);
    const [errorMessages, setErrorMessages] = useState({});
    const buyMetalOptions = useMemo(
        () => buildBuyMetalOptions(metalsenabled),
        [metalsenabled]
    );
    const metalCostPreview = useMemo(
        () => buildMetalCostPreview(formData),
        [formData.ratePerGms, formData.purity, formData.inputPurity, formData.metal_weight]
    );

    useEffect(() => {
        if (!open) {
            setFormData(initialForm);
            setNewTag('');
            setErrorMessages({});
        }
    }, [open]);

    useEffect(() => {
        if (!open || !editData) return;

        const {
            name,
            metalname,
            purity,
            metal_weight,
            desc,
            tags,
            carat,
            bullion,
            taxPercentage,
            productCharges = {},
            url,
            images,
            videos,
            productImages,
            productVideos,
        } = editData;

        const bullionId = getBullionId(bullion);
        const bullionObj = typeof bullion === 'object' ? bullion : null;
        const selectedOption = findMetalOptionForEdit(buyMetalOptions, editData);
        const resolvedBullion = selectedOption?.bullion || bullionObj;
        const storedRate = getStoredProductRate(editData);
        const ratePerGms = selectedOption
            ? getRatePerGram(selectedOption)
            : storedRate || 0;
        const basePurity = resolvedBullion?.purity ?? '';
        const selectedPurity = purity ?? resolvedBullion?.purity ?? '';

        const existingImages = productImages?.length
            ? productImages
            : images?.length
                ? images.filter((item) => {
                    const src = typeof item === 'string' ? item : item?.url;
                    return src && !isVideoUrl(src);
                })
                : url && !isVideoUrl(url)
                    ? [url]
                    : [];

        const existingVideos = productVideos?.length
            ? productVideos
            : videos?.length
                ? videos
                : url && isVideoUrl(url)
                    ? [url]
                    : [];

        const newForm = {
            ...initialForm,
            id: editData._id,
            name: name || '',
            metalName: metalname || resolvedBullion?.metalName || '',
            purity: basePurity,
            inputPurity: selectedPurity,
            karat: carat || resolvedBullion?.karat || '',
            metal_weight: metal_weight || '',
            desc: desc || '',
            tags: tags || [],
            bullion: bullionId,
            bullionOptionKey: selectedOption?.optionKey || '',
            ratePerGms,
            tax: taxPercentage || 0,
            productImages: existingImages,
            productVideos: existingVideos,
            percentageCharges: productCharges.makingchargesType === 'percentage',
            makingCharges: Number(productCharges.makingCharges).toFixed(2) || 0,
            makingChargesPercent: productCharges.makingChargesPercentage || 0,
            discountMakingPercent: productCharges.makingDiscountPercentage || 0,
            gstMaking: productCharges.gstMakingPercentage || 0,
            deliveryCharges: Number(productCharges.deliveryCharges).toFixed(2) || 0,
            discountonDelivery: productCharges.deliveryDiscountPercentage || 0,
            gstonDelivery: productCharges.gstDeliveryPercentage || 0,
            packingCharges: Number(productCharges.packingCharges).toFixed(2) || 0,
            discountonPacking: productCharges.packingDiscountPercentage || 0,
            gstonPacking: productCharges.gstPackingPercentage || 0,
            finalMakingCharges: Number(productCharges.discountedMakingCharges).toFixed(2) || 0,
            finalCeliveryCharges: Number(productCharges.discountedDeliveryCharges).toFixed(2) || 0,
            finalPackingCharges: Number(productCharges.discountedPackingCharges).toFixed(2) || 0,
            hallmarkCharges: Number(productCharges.hallmarkCharges).toFixed(2) ?? 0,
            otherCharges: Number(productCharges.otherCharges).toFixed(2) ?? 0,
            discountProduct: productCharges.productDiscountPercentage || 0,
            makingchargesType: productCharges.makingchargesType || 'lumson',
        };

        setFormData(newForm);

        updateCalculation({
            metalRate: ratePerGms,
            purity: getNumeric(basePurity),
            inputPurity: getNumeric(selectedPurity),
            metal_weight: getNumeric(newForm.metal_weight),
            deliveryCharges: getNumeric(newForm.deliveryCharges),
            packingCharges: getNumeric(newForm.packingCharges),
            makingCharges: getNumeric(newForm.makingCharges),
            deliveryDiscountPercentage: getNumeric(newForm.discountonDelivery),
            packingDiscountPercentage: getNumeric(newForm.discountonPacking),
            makingDiscountPercentage: getNumeric(newForm.discountMakingPercent),
            gstDeliveryPercentage: getNumeric(newForm.gstonDelivery),
            gstPackingPercentage: getNumeric(newForm.gstonPacking),
            gstMakingPercentage: getNumeric(newForm.gstMaking),
            taxPercentage: getNumeric(newForm.tax),
            productDiscountPercentage: getNumeric(newForm.discountProduct),
            hallmarkCharges: getNumeric(newForm.hallmarkCharges),
            otherCharges: getNumeric(newForm.otherCharges),
            makingPercentage: getNumeric(newForm.makingChargesPercent),
            isChecked: newForm.percentageCharges,
        });
    }, [open, editData, buyMetalOptions, updateCalculation]);

    useEffect(() => {
        updateCalculation({
            metalRate: getNumeric(formData.ratePerGms),
            purity: getNumeric(formData?.bullion?.purity || formData.purity),
            inputPurity: getNumeric(formData.inputPurity),
            metal_weight: getNumeric(formData.metal_weight),
            deliveryCharges: getNumeric(formData.deliveryCharges),
            packingCharges: getNumeric(formData.packingCharges),
            makingCharges: getNumeric(formData.makingCharges),
            deliveryDiscountPercentage: getNumeric(formData.discountonDelivery),
            packingDiscountPercentage: getNumeric(formData.discountonPacking),
            makingDiscountPercentage: getNumeric(formData.discountMakingPercent),
            gstDeliveryPercentage: getNumeric(formData.gstonDelivery),
            gstPackingPercentage: getNumeric(formData.gstonPacking),
            gstMakingPercentage: getNumeric(formData.gstMaking),
            taxPercentage: getNumeric(formData.tax),
            productDiscountPercentage: getNumeric(formData.discountProduct),
            hallmarkCharges: getNumeric(formData.hallmarkCharges),
            otherCharges: getNumeric(formData.otherCharges),
            makingPercentage: getNumeric(formData.makingChargesPercent),
            isChecked: formData.percentageCharges
        });
    }, [formData, updateCalculation]);

    useEffect(() => {
        setFormData(prev => {
            const updated = {
                ...prev,
                finalProductCost: Number(calculationResult.finalPrice || 0).toFixed(2),
                finalMakingCharges: Number(calculationResult.finalMakingCharges || 0).toFixed(2),
                finalCeliveryCharges: Number(calculationResult.finalDeliveryCharges || 0).toFixed(2),
                finalPackingCharges: Number(calculationResult.finalPackingCharges || 0).toFixed(2),
                metalCost: Number(calculationResult.productCost || 0).toFixed(2),
            };
            const keysToCheck = ['finalProductCost', 'finalMakingCharges', 'finalCeliveryCharges', 'finalPackingCharges', 'metalCost'];
            return keysToCheck.some(key => String(prev[key]) !== String(updated[key])) ? updated : prev;
        });
    }, [calculationResult]);

    const handleChange = useCallback((e) => {
        const { name, type, checked, value } = e?.target || {};

        if (name === 'percentageCharges') {
            setFormData(prev => ({
                ...prev,
                percentageCharges: checked,
                makingchargesType: checked ? 'percentage' : 'lumson',
                discountMakingPercent: checked ? 0 : prev.discountMakingPercent,
                gstMaking: checked ? 0 : prev.gstMaking,
                makingCharges: checked ? 0 : prev.makingCharges,
            }));
            return;
        }

        if (name === 'bullionOptionKey') {
            const selectedBullion = buyMetalOptions.find(m => m.optionKey === value);
            if (selectedBullion) {
                const { bullion } = selectedBullion;
                const buyRate = selectedBullion.buyRatePerGram || getRatePerGram(selectedBullion);
                setFormData(prev => ({
                    ...prev,
                    inputPurity: bullion.purity,
                    bullionOptionKey: selectedBullion.optionKey,
                    bullion: bullion._id,
                    purity: bullion.purity,
                    karat: bullion.karat,
                    metalName: bullion.metalName,
                    ratePerGms: buyRate,
                }));
                return;
            }
        }

        if (name === 'metalName') {
            const selectedMetal = buyMetalOptions.find(m => m.bullion.metalName === value);
            if (selectedMetal) {
                const { bullion } = selectedMetal;
                const buyRate = selectedMetal.buyRatePerGram || getRatePerGram(selectedMetal);
                setFormData(prev => ({
                    ...prev,
                    metalName: bullion.metalName,
                    purity: bullion.purity,
                    karat: bullion.karat,
                    bullion: bullion._id,
                    bullionOptionKey: selectedMetal.optionKey,
                    ratePerGms: buyRate,
                }));
                return;
            }
        }

        if (name === 'hallmarkCharges' || name === 'otherCharges') {
            const label = name === 'hallmarkCharges' ? 'Hallmark Charges' : 'Other Charges';
            const fieldError = getOptionalChargeError(label, value);
            setErrorMessages(prev => ({
                ...prev,
                [name]: fieldError,
            }));
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    }, [buyMetalOptions]);

    const addTag = () => {
        const tag = newTag.trim();
        if (!tag) return;
        setFormData(prev => {
            const tags = Array.isArray(prev.tags) ? prev.tags : [];
            if (tags.includes(tag)) return prev;
            return { ...prev, tags: [...tags, tag] };
        });
        setNewTag('');
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: (prev.tags || []).filter(tag => tag !== tagToRemove),
        }));
    };

    const validations = [
        { tagid: 'tags', value: (formData.tags || []).join(', '), required: false, regex_name: 'free_text', errmsg: 'When provided, tags must be between 2 and 50 characters (letters and numbers allowed).' },
        { tagid: 'karat', value: formData.karat, regex_name: 'min_number', errmsg: 'Please enter valid carat details', required: true, min: 1 },
        { tagid: 'purity', value: formData.purity, regex_name: 'max_number', errmsg: 'Please enter valid purity.', required: true, min: 1, max: 999 },
        { tagid: 'metal_weight', value: formData.metal_weight, regex_name: 'min_number', errmsg: 'Please enter weight.', required: true, min: 0.001 },
        { tagid: 'desc', value: formData.desc, regex_name: 'free_text', errmsg: 'Please enter product description', required: true, min: 1, max: 3000 },
    ];

    const appendMediaToFormData = (data, files, fieldName) => {
        (files || []).forEach((file) => {
            if (file instanceof File) {
                data.append(fieldName, file);
            } else if (typeof file === 'string' && file.trim()) {
                data.append(fieldName, file);
            }
        });
    };

    const handleSubmit = () => {
        setErrorMessages({});
        const result = validateInputs(validations, {
            error_display_html: true,
            highlight_all_errors: true,
        });

        if (result === true) {
            const hallmarkError = getOptionalChargeError('Hallmark Charges', formData.hallmarkCharges);
            const otherError = getOptionalChargeError('Other Charges', formData.otherCharges);
            if (hallmarkError || otherError) {
                setErrorMessages(prev => ({
                    ...prev,
                    hallmarkCharges: hallmarkError,
                    otherCharges: otherError,
                }));
                return;
            }

            const mediaError = validateProductMedia(formData.productImages, formData.productVideos);
            if (mediaError) {
                toast.error(mediaError);
                return;
            }

            const data = new FormData();
            const normalizedHallmarkCharges = Number(formData.hallmarkCharges || 0);
            const normalizedOtherCharges = Number(formData.otherCharges || 0);

            const payloadFields = {
                name: formData.name,
                metal_weight: formData.metal_weight,
                id: formData.id ?? 'null',
                desc: formData.desc,
                metalname: formData.metalName,
                purity: formData.inputPurity,
                termandconditions: formData.termandconditions ?? 'undefined',
                tags: JSON.stringify(formData.tags || []),
                makingCharges: formData.makingCharges,
                gstMakingPercentage: formData.gstMaking,
                makingDiscountPercentage: formData.discountMakingPercent,
                deliveryCharges: formData.deliveryCharges,
                deliveryDiscountPercentage: formData.discountonDelivery,
                gstDeliveryPercentage: formData.gstonDelivery,
                packingCharges: formData.packingCharges,
                packingDiscountPercentage: formData.discountonPacking,
                gstPackingPercentage: formData.gstonPacking,
                discountedMakingCharges: formData.finalMakingCharges,
                discountedPackingCharges: formData.finalPackingCharges,
                discountedDeliveryCharges: formData.finalCeliveryCharges,
                productDiscountPercentage: formData.discountProduct,
                makingChargesPercentage: formData.makingChargesPercent,
                taxPercentage: formData.tax,
                hallmarkCharges: normalizedHallmarkCharges,
                otherCharges: normalizedOtherCharges,
                bullion: formData.bullion,
                carat: formData.karat,
                makingchargesType: formData.makingchargesType,
            };

            for (const [key, val] of Object.entries(payloadFields)) {
                if (val !== undefined && val !== null) {
                    data.append(key, String(val));
                }
            }

            appendMediaToFormData(data, formData.productImages, 'file');
            appendMediaToFormData(data, formData.productVideos, 'videos');

            onSubmit(data);
            onClose();
            setFormData(initialForm);
        } else {
            const errorObj = result.reduce((acc, curr) => {
                acc[curr.tagid] = curr.errmsg;
                return acc;
            }, {});
            setErrorMessages(errorObj);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #752ADA 0%, #9b5de5 100%)',
                    color: '#fff',
                    py: 2.5,
                }}
            >
                <Typography variant="h6" fontWeight={700}>
                    {editData ? 'Edit Product' : 'Add Product'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Fill in product details, pricing, and upload images & videos
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ px: 3, py: 3, bgcolor: '#f8f9fc' }}>
                <SectionCard
                    icon={Inventory2OutlinedIcon}
                    title="Basic Information"
                    subtitle="Name and description shown to customers"
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth size="small" label="Product Name" name="name"
                                value={formData.name} onChange={handleChange} required
                                error={!!errorMessages.name}
                                helperText={errorMessages.name || 'Enter a clear product name'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                label="Description"
                                name="desc"
                                value={formData.desc}
                                onChange={handleChange}
                                required
                                error={!!errorMessages.desc}
                                helperText={errorMessages.desc || 'Describe the product features, design, and details'}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    icon={DiamondOutlinedIcon}
                    title="Metal Details"
                    subtitle="Product metal cost is calculated from the selected rate and product weight"
                >
                    <Alert
                        icon={<InfoOutlinedIcon fontSize="inherit" />}
                        severity="info"
                        sx={{
                            mb: 2.5,
                            bgcolor: 'rgba(117,42,218,0.06)',
                            color: '#4c1d95',
                            border: '1px solid rgba(117,42,218,0.18)',
                            '& .MuiAlert-icon': { color: '#752ADA' },
                        }}
                    >
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                            How metal cost works
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
                            <strong>General Formula</strong>
                            <br />
                            Purity Rate = (Base Rate × Selected Purity) / Base Purity
                            <br />
                            Metal Cost = Product Weight × Purity Rate
                            <br /><br />
                            1. Select metal and rate entry to apply it as <strong>base rate</strong>.
                            <br />
                            2. Set <strong>selected purity</strong> for the product.
                            <br />
                            3. Enter <strong>weight in grams</strong> to get metal cost for final pricing.
                        </Typography>
                    </Alert>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" variant="outlined">
                                <InputLabel id="bullion-select-label" shrink>
                                    Select Metal & Rate
                                </InputLabel>
                                <Select
                                    labelId="bullion-select-label"
                                    id="bullion-select"
                                    name="bullionOptionKey"
                                    value={formData.bullionOptionKey || ''}
                                    onChange={handleChange}
                                    label="Select Metal & Rate"
                                    displayEmpty
                                    notched
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <Typography variant="body2" color="text.secondary">
                                                    Choose a metal rate entry
                                                </Typography>
                                            );
                                        }
                                        const item = buyMetalOptions.find((m) => m.optionKey === selected);
                                        return item ? formatBuyMetalLabel(item) : 'Selected metal';
                                    }}
                                    MenuProps={{
                                        disablePortal: true,
                                        PaperProps: {
                                            sx: { maxHeight: 280, mt: 0.5 },
                                        },
                                    }}
                                    sx={{
                                        bgcolor: '#fff',
                                        '& .MuiSelect-select': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            minHeight: '1.4375em',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Select any available metal rate</em>
                                    </MenuItem>
                                    {buyMetalOptions.length === 0 && (
                                        <MenuItem value="__empty" disabled>
                                            No metal rates available
                                        </MenuItem>
                                    )}
                                    {buyMetalOptions.map((item) => (
                                        <MenuItem key={item.optionKey} value={item.optionKey}>
                                            {formatBuyMetalLabel(item)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    All available rate entries are shown here and used directly for costing.
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {metalCostPreview && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        bgcolor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={700} color="#1f2937" sx={{ mb: 1.5 }}>
                                        Live Calculation
                                    </Typography>

                                    <Box
                                        sx={{
                                            p: 1.5,
                                            mb: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: '#fff',
                                            border: '1px solid #e5e7eb',
                                            fontFamily: 'monospace',
                                            fontSize: 14,
                                            lineHeight: 1.8,
                                            color: '#334155',
                                        }}
                                    >
                                        <Box>
                                            Purity Rate = (Base Rate × Selected Purity) / Base Purity
                                        </Box>
                                        <Box sx={{ mt: 0.5 }}>
                                            = (₹{formatAmount(metalCostPreview.baseRate)} × {metalCostPreview.selectedPurity}) / {metalCostPreview.basePurity}
                                        </Box>
                                        <Box sx={{ mt: 0.5, fontWeight: 700, color: '#752ADA' }}>
                                            = ₹{formatAmount(metalCostPreview.purityRate)} per gram
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 1.5,
                                            mb: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: '#fff',
                                            border: '1px solid #e5e7eb',
                                            fontFamily: 'monospace',
                                            fontSize: 14,
                                            lineHeight: 1.8,
                                            color: '#334155',
                                        }}
                                    >
                                        <Box>Metal Cost = Product Weight × Purity Rate</Box>
                                        <Box sx={{ mt: 0.5 }}>
                                            = {metalCostPreview.weight || 0} × ₹{formatAmount(metalCostPreview.purityRate)}
                                        </Box>
                                        <Box sx={{ mt: 0.5, fontWeight: 700, color: '#752ADA' }}>
                                            = ₹{formatAmount(metalCostPreview.metalCost)}
                                        </Box>
                                    </Box>

                                    <Typography variant="caption" color="text.secondary">
                                        Example: ₹9000 × 916 / 999 = ₹8,252.25 per gram. For 1g product, metal cost = 1 × 8,252.25 = ₹8,252.25.
                                        This metal cost is used while calculating final product price.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ min: 0 }}
                                label="Purity" name="inputPurity" value={formData.inputPurity}
                                onChange={handleChange} required
                                helperText="Selected purity used in purity rate formula"
                                InputLabelProps={{
                                    shrink:
                                        formData.inputPurity !== "" &&
                                        formData.inputPurity !== null &&
                                        formData.inputPurity !== undefined,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth size="small" type="number"
                                inputProps={{ min: 0.001, step: 0.001 }}
                                label="Weight (Gms)" name="metal_weight"
                                value={formData.metal_weight} onChange={handleChange}
                                error={!!errorMessages.metal_weight}
                                helperText={errorMessages.metal_weight || 'Used in Metal Cost = Weight × Purity Rate'}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ min: 0 }}
                                label="Metal Cost (₹)" name="metalCost"
                                value={formData.metalCost} disabled
                                helperText={`Base purity: ${formData.purity || '-'} | Auto: Weight × Purity Rate`}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    icon={PaymentsOutlinedIcon}
                    title="Charges & Pricing"
                    subtitle="Each charge type has its own row — enter base amount, discount, and tax separately"
                >
                    {chargeSections.map((section) => (
                        <ChargeRow
                            key={section.key}
                            title={section.title}
                            subtitle={section.subtitle}
                            accent={section.accent}
                            topContent={section.key === 'making' ? (
                                <Box>
                                    <FormControlLabel
                                        label="Use percentage-based making charges (instead of fixed ₹ amount)"
                                        control={
                                            <Checkbox
                                                name="percentageCharges"
                                                checked={formData.percentageCharges}
                                                onChange={handleChange}
                                                sx={{ color: '#752ADA', '&.Mui-checked': { color: '#752ADA' } }}
                                            />
                                        }
                                    />
                                    {formData.percentageCharges && (
                                        <Box sx={{ maxWidth: 320, mt: 1 }}>
                                            <ChargeField
                                                label="Making Charges (%)"
                                                name="makingChargesPercent"
                                                value={formData.makingChargesPercent}
                                                onChange={handleChange}
                                                helperText="Percentage of metal cost used as making charge"
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ) : null}
                        >
                            {section.fields.map((field) => {
                                const isPercentDisabled =
                                    section.percentDisabledFields?.includes(field.name) &&
                                    formData.percentageCharges;

                                return (
                                    <ChargeField
                                        key={field.name}
                                        label={field.label}
                                        name={field.name}
                                        value={isPercentDisabled ? 0 : formData[field.name] || ''}
                                        onChange={handleChange}
                                        disabled={isPercentDisabled || field.readOnly}
                                        readOnly={field.readOnly}
                                        accent={section.accent}
                                        error={!!errorMessages[field.name]}
                                        helperText={errorMessages[field.name] || field.helperText}
                                    />
                                );
                            })}
                        </ChargeRow>
                    ))}

                    <ChargeRow
                        title="Additional Charges"
                        subtitle="Optional flat charges included in final product price"
                        accent="#7c3aed"
                        columns={2}
                    >
                        <ChargeField
                            label="Hallmark Charges (₹)"
                            name="hallmarkCharges"
                            value={formData.hallmarkCharges ?? ''}
                            onChange={handleChange}
                            error={!!errorMessages.hallmarkCharges}
                            helperText={errorMessages.hallmarkCharges || 'Optional fixed amount'}
                            step={0.01}
                        />
                        <ChargeField
                            label="Other Charges (₹)"
                            name="otherCharges"
                            value={formData.otherCharges ?? ''}
                            onChange={handleChange}
                            error={!!errorMessages.otherCharges}
                            helperText={errorMessages.otherCharges || 'Optional fixed amount'}
                            step={0.01}
                        />
                    </ChargeRow>

                    <ChargeRow
                        title="Product Pricing Summary"
                        subtitle="Overall product discount and tax applied on top of metal cost and all charges"
                        accent="#ea580c"
                        columns={3}
                    >
                        <ChargeField
                            label="Discount on Product (%)"
                            name="discountProduct"
                            value={formData.discountProduct || ''}
                            onChange={handleChange}
                            error={!!errorMessages.discountProduct}
                            helperText={errorMessages.discountProduct || 'Discount applied on metal cost'}
                        />
                        <ChargeField
                            label="Product Tax (%)"
                            name="tax"
                            value={formData.tax || ''}
                            onChange={handleChange}
                            error={!!errorMessages.tax}
                            helperText={errorMessages.tax || 'Overall tax percentage on product'}
                        />
                        <ChargeField
                            label="Final Product Cost (₹)"
                            name="finalProductCost"
                            value={formData.finalProductCost || ''}
                            onChange={handleChange}
                            disabled
                            readOnly
                            accent="#752ADA"
                            helperText="Metal cost + charges + tax (auto-calculated)"
                        />
                    </ChargeRow>
                </SectionCard>

                <SectionCard
                    icon={LocalOfferOutlinedIcon}
                    title="Tags"
                    subtitle="Add searchable keywords for this product"
                >
                    <Box display="flex" alignItems="flex-start" gap={1}>
                        <TextField
                            fullWidth size="small" label="Add Tag"
                            placeholder="e.g. ring, wedding, 22k"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            error={!!errorMessages.tags}
                            helperText={errorMessages.tags}
                        />
                        <IconButton
                            onClick={addTag}
                            sx={{
                                mt: 0.5,
                                bgcolor: '#752ADA',
                                color: '#fff',
                                '&:hover': { bgcolor: '#5f1fb8' },
                            }}
                        >
                            <CiCirclePlus size={22} />
                        </IconButton>
                    </Box>
                    {formData.tags?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {formData.tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    onDelete={() => removeTag(tag)}
                                    deleteIcon={<CiCircleRemove />}
                                    sx={{
                                        bgcolor: 'rgba(117,42,218,0.1)',
                                        color: '#752ADA',
                                        fontWeight: 500,
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </SectionCard>

                <SectionCard
                    icon={PermMediaOutlinedIcon}
                    title="Product Media"
                    subtitle="Upload multiple images and videos to showcase the product"
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Product Images
                            </Typography>
                            <FileUploaderMultiMedia
                                value={formData.productImages}
                                onChange={(files) => {
                                    const validFiles = files.filter(isValidImageFile);
                                    if (validFiles.length !== files.length) {
                                        toast.error('Only JPG/PNG images up to 500 KB are allowed');
                                    }
                                    setFormData(prev => ({ ...prev, productImages: validFiles }));
                                }}
                                accept={IMAGE_ACCEPT}
                                maxFiles={5}
                                maxFileSize={DEFAULT_MAX_FILE_SIZE}
                                mediaType="image"
                                title="Add Product Images"
                                helperText="JPG, PNG only — max 500 KB each — up to 5 images"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Product Videos
                            </Typography>
                            <FileUploaderMultiMedia
                                value={formData.productVideos}
                                onChange={(files) => {
                                    const validFiles = files.filter(isValidVideoFile);
                                    if (validFiles.length !== files.length) {
                                        toast.error('Only MP4 videos up to 5 MB are allowed');
                                    }
                                    setFormData(prev => ({ ...prev, productVideos: validFiles }));
                                }}
                                accept={VIDEO_ACCEPT}
                                maxFiles={3}
                                maxFileSize={VIDEO_MAX_FILE_SIZE}
                                mediaType="video"
                                title="Add Product Videos"
                                helperText="MP4 only — max 5 MB each — up to 3 videos"
                            />
                        </Grid>
                    </Grid>
                </SectionCard>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: '#fff',
                    borderTop: '1px solid #e8eaf0',
                    gap: 1,
                }}
            >
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        bgcolor: '#752ADA',
                        px: 3,
                        '&:hover': { bgcolor: '#5f1fb8' },
                    }}
                >
                    {editData ? 'Update Product' : 'Save Product'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductDialog;
